import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import DataTable from '../../../sharedModules/dataTable';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import SubmitCachedDataModal from '../components/SubmitCachedDataModal';

import { BackflowJobGroup, BackflowJobItem } from '../../model';

interface CachedJobGroup {
  jobGroup: BackflowJobGroup;
  jobItems: BackflowJobItem[];
  cachedAt: string;
  unsyncedCount: number;
}

const UnsubmittedTestsPage: React.FunctionComponent = () => {
  const history = useHistory();

  const [cachedJobGroups, setCachedJobGroups] = useState<CachedJobGroup[]>([]);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedJobGroup, setSelectedJobGroup] = useState<any>(null);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const loadCachedJobGroups = () => {
    try {
      const allKeys = Object.keys(localStorage);
      const jobGroupKeys = allKeys.filter((key) => key.startsWith('backflowJobGroup_'));

      const cached: CachedJobGroup[] = jobGroupKeys
        .map((key) => {
          try {
            const data = localStorage.getItem(key);
            if (!data) return null;

            const parsed = JSON.parse(data);

            // Count items that have device changes OR log changes needing sync
            const unsyncedCount =
              parsed.jobItems?.filter((item: any) => item.backflow_job_item?.needsSync || item.backflow_log?.needsSync)
                .length || 0;

            return {
              jobGroup: parsed.jobGroup,
              jobItems: parsed.jobItems,
              cachedAt: parsed.cachedAt,
              unsyncedCount,
            };
          } catch (error) {
            console.error(`Failed to parse localStorage key ${key}:`, error);
            return null;
          }
        })
        .filter((item): item is CachedJobGroup => item !== null)
        .sort((a, b) => new Date(b.cachedAt).getTime() - new Date(a.cachedAt).getTime()); // Most recent first

      setCachedJobGroups(cached);
    } catch (error) {
      console.error('Failed to load cached job groups:', error);
    }
  };

  useEffect(() => {
    loadCachedJobGroups();
  }, []);

  const handleClearCache = (jobGroupId: string) => {
    try {
      const cacheKey = `backflowJobGroup_${jobGroupId}`;
      localStorage.removeItem(cacheKey);
      loadCachedJobGroups(); // Refresh the list
    } catch (error) {
      console.error('Failed to clear cache:', error);
    }
  };

  const handleOpenSubmitModal = (row: CachedJobGroup) => {
    // Get the full job group data from localStorage
    const cacheKey = `backflowJobGroup_${row.jobGroup.id}`;
    const cachedData = localStorage.getItem(cacheKey);
    if (cachedData) {
      const jobGroupData = JSON.parse(cachedData);
      setSelectedJobGroup(jobGroupData);
      setModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedJobGroup(null);
  };

  const handleSubmitSuccess = () => {
    loadCachedJobGroups(); // Refresh the list after successful submission
  };

  const columns = [
    {
      name: 'Order',
      selector: (row: CachedJobGroup) => row.jobGroup.order_no,
      sortable: true,
      width: '120px',
    },
    {
      name: 'Customer',
      selector: (row: CachedJobGroup) => row.jobGroup.company_name,
      sortable: true,
      grow: 2,
    },
    {
      name: 'Address',
      selector: (row: CachedJobGroup) => row.jobGroup.address1,
      sortable: true,
      grow: 2,
    },
    {
      name: 'Devices',
      selector: (row: CachedJobGroup) => row.jobItems?.length || 0,
      sortable: true,
      width: '100px',
    },
    {
      name: 'Cached At',
      selector: (row: CachedJobGroup) => row.cachedAt,
      sortable: true,
      width: '180px',
      cell: (row: CachedJobGroup) => format(new Date(row.cachedAt), 'MMM dd, yyyy h:mm a'),
    },
    {
      name: 'Actions',
      width: '150px',
      cell: (row: CachedJobGroup) => (
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenSubmitModal(row);
          }}
        >
          Review
        </Button>
      ),
    },
  ];

  return (
    <Grid container>
      <Grid item xs={12}>
        <PageHeader mainTitle="Backflow Testing" subTitle="Unsubmitted Backflow Work Orders" />
      </Grid>

      {!isOnline && (
        <Grid item xs={12} style={{ marginTop: 20 }}>
          <Alert severity="warning">
            You are currently offline. Any unsubmitted logs shown below are saved on your device and can be submitted
            when you're connected.
          </Alert>
        </Grid>
      )}

      {cachedJobGroups.length === 0 && (
        <Grid item xs={12} style={{ marginTop: 20 }}>
          <Alert severity="info">No unsubmitted test logs found. You're all caught up!</Alert>
        </Grid>
      )}

      {cachedJobGroups.length > 0 && (
        <>
          <Grid item xs={12} style={{ marginTop: 20, marginBottom: 10 }}>
            <Typography variant="body2" color="textSecondary">
              This page shows all backflow work orders saved on your device from when you were working offline.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <DataTable
                  title=""
                  columns={columns}
                  data={cachedJobGroups}
                  dense
                  highlightOnHover
                  pointerOnHover
                  pagination
                  paginationPerPage={25}
                  paginationRowsPerPageOptions={[25, 50, 100]}
                  noHeader
                />
              </CardContent>
            </Card>
          </Grid>
        </>
      )}

      {/* Submit Modal */}
      {selectedJobGroup && (
        <SubmitCachedDataModal
          open={modalOpen}
          onClose={handleModalClose}
          jobGroupId={selectedJobGroup.jobGroup.id}
          jobGroupData={selectedJobGroup}
          onSubmitSuccess={handleSubmitSuccess}
        />
      )}
    </Grid>
  );
};

export default UnsubmittedTestsPage;
